import { BuildingIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { findCustomerMatches, type Customer } from '@/api/customers'
import { Button } from '@/components/ui/button'
import { formatAddress } from '@/components/customers/address'

/** Long enough that typing a company name is one request, short enough to answer while typing. */
const DEBOUNCE_MS = 400

/** Below this a name matches half the list, and a hint that always fires is one nobody reads. */
const MIN_NAME_LENGTH = 3

/**
 * "This company is already in the master data."
 *
 * The master data comes out of Odoo, and a company typed in free at a stand that is already in
 * there is exactly the pile the overview counts under "Nicht übernommen". Catching it while the
 * name is being typed costs one click; catching it a week later costs a search, a comparison and
 * a decision.
 *
 * Only while the lead is **not** linked yet: once a company has been taken from the master data,
 * the hint would be pointing at the record the form was filled from.
 *
 * A hint, never a block. The company at the stand may genuinely be a second one of the same name,
 * and somebody standing in front of a customer is not the person to argue with about it.
 */
export function CustomerMatchHint({
  companyName,
  customerNumber,
  linked,
  onTake,
  onShowAll,
}: {
  companyName: string
  customerNumber: string | null
  /** True once the form was filled from a master record; the hint then has nothing to say. */
  linked: boolean
  onTake: (customer: Customer) => void
  /** More than one candidate is a choice, and there is already a screen for making it. */
  onShowAll: () => void
}) {
  const { t, i18n } = useTranslation()
  const language = i18n.resolvedLanguage ?? 'de'
  /** Which request produced these. Comparing it with the current one is what clears them. */
  const [loaded, setLoaded] = useState<{ request: string; customers: Customer[] }>({
    request: '',
    customers: [],
  })

  const name = companyName.trim()
  const number = customerNumber?.trim() ?? ''
  const worthAsking = !linked && (name.length >= MIN_NAME_LENGTH || number !== '')
  // JSON rather than a joined string: both halves are free text, so any separator could occur
  // inside them too. Asked for as one value so the effect runs on exactly what it sends.
  const request = worthAsking ? JSON.stringify([name, number]) : ''

  // Derived rather than cleared in the effect below: the moment the name changes, what is on
  // screen belongs to the previous one, and an answer that arrives for a name nobody is typing any
  // more must not be painted either.
  const matches = loaded.request === request ? loaded.customers : []

  useEffect(() => {
    if (request === '') {
      return
    }
    const [askedName, askedNumber] = JSON.parse(request) as [string, string]
    const controller = new AbortController()
    const timer = setTimeout(() => {
      findCustomerMatches(askedName, askedNumber || null, controller.signal)
        .then((found) => {
          if (!controller.signal.aborted) {
            setLoaded({ request, customers: found.customers })
          }
        })
        // A hint that cannot be fetched is a hint that is not shown. Nothing here is worth an
        // error over a form somebody is in the middle of filling in.
        .catch(() => undefined)
    }, DEBOUNCE_MS)

    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [request])

  if (matches.length === 0) {
    return null
  }

  const [first] = matches
  const only = matches.length === 1

  return (
    <div className="bg-muted/50 mt-2 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border p-3">
      <BuildingIcon className="text-muted-foreground size-4 shrink-0" aria-hidden />
      <div className="min-w-48 flex-1">
        <p className="text-sm font-medium">
          {only ? t('leadForm.matchOne') : t('leadForm.matchMany', { count: matches.length })}
        </p>
        <p className="text-muted-foreground text-xs">
          {only
            ? [first.companyName, first.customerNumber, formatAddress(first, language)]
                .filter(Boolean)
                .join(' · ')
            : t('leadForm.matchHint')}
        </p>
      </div>
      {only ? (
        <Button type="button" variant="outline" size="sm" onClick={() => onTake(first)}>
          {t('leadForm.matchTake')}
        </Button>
      ) : (
        <Button type="button" variant="outline" size="sm" onClick={onShowAll}>
          {t('leadForm.matchShowAll')}
        </Button>
      )}
    </div>
  )
}
